import { api } from '../api'

export const aiGenerationApi = api.injectEndpoints({
    endpoints: (builder) => ({
        generatePrompt: builder.mutation({
            query: (formData) => {
                return {
                    url: '/ai/image/generate/prompt',
                    method: 'POST',
                    body: formData,
                };
            },
        }),    
        generateCreativeVariations: builder.mutation({
            query: (formData) => ({
                url: '/ai/image/generate',
                method: 'POST',
                body: formData,
            }),
        }),
        removeBackground: builder.mutation({
            query: (payload) => ({
                url: '/remove-background/',
                method: 'POST',
                body: payload,
            }),
        }),
        generateBackgroundPrompt: builder.mutation({
            query: (formData) => {
                return {
                    url: '/ai/image/generate/background/prompt',
                    method: 'POST',
                    body: formData,
                };
            },
        }), 
        generateBackgroundImage: builder.mutation({
            query: (payload) => {
                return {
                    url: '/ai/image/generate/background/',
                    method: 'POST',
                    body: payload,
                };
            },
        }), 
        getBackgroundImageStatus: builder.query({
            async queryFn(backgroundImageIds, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const requests = backgroundImageIds?.map((id) =>
                        fetchWithBQ(`/ai/background/image/status/${id}`)
                    );

                    const results = await Promise.all(requests);

                    const hasErrors = results.some((res) => res.error);
                    if (hasErrors) {
                        return { error: { message: 'One or more image status requests failed.' } };
                    }

                    const data = results.map((res) => res.data.data); // { id, url }

                    return { data };
                } catch (err) {
                    return { error: err };
                }
            },
        }),
        uploadManual: builder.mutation({
            query: (formData) => ({
                url: '/ai/video/upload',
                method: 'POST',
                body: formData,
            }),

            transformResponse: (response) => response.data,
        }),
        generateVideoLink: builder.mutation({
            query: (payload) => {
                return {
                    url: '/ai/video/private/generate/links',
                    method: 'POST',
                    body: payload,
                };
            },
            transformResponse: (response) => response.data,
        }), 
        updateVideoLink: builder.mutation({
            query: ({payload, linkId}) => {
                return {
                    url: `/ai/video/update/links/${linkId}`,
                    method: 'PUT',
                    body: payload,
                };
            },
            transformResponse: (response) => response.data,
        }), 
        generateVideoScript: builder.mutation({
            query: (payload) => {
                return {
                    url: '/ai/video/private/script',
                    method: 'POST',
                    body: payload,
                };
            },
            transformResponse: (response) => response.data,
        }), 
        getVideoAvatars: builder.query({
            query: (videoAvatarFilters = {}) => {

              let query = "";
          
              const entries = Object.entries(videoAvatarFilters);
              if (entries.length > 0) {
                query =
                  "?" +
                  entries
                    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                    .join("&");
              }
          
              return {
                url: `/ai/video/personas/paginated${query}`,
              };
            },
            transformResponse: (response) => response.data,
        }),    
        getVideoVoices: builder.query({
            query: () => ({
                url: `/ai/video/voices`,
            }),
            transformResponse: (response, meta, arg) => response.data
        }),
        getVideoMusics: builder.query({
            query: ({ category, search, page, page_size }) => {
              let url = `/ai/video/musics?page=${page}&page_size=${page_size}`;
              if (category) url += `&category=${category}`;
              if (search) url += `&search=${encodeURIComponent(search)}`;
              return {
                url,
              };
            },
            transformResponse: (response) => response.data,
        }),
        generateVideoPreviews: builder.mutation({
            query: (payload) => {
                return {
                    url: '/ai/video/link_to_videos/preview_list_async',
                    method: 'POST',
                    body: payload,
                };
            },
            transformResponse: (response) => response.data,
        }),
        getVideoStatus: builder.query({
            query: ({previewId}) => ({
                url: `/ai/video/private/link_to_videos/status/${previewId}`,
            }),
            transformResponse: (response, meta, arg) => response.data
        }),
        renderVideo: builder.mutation({
            query: ({payload, id}) => {
                return {
                    url:  `/ai/video/private/link_to_videos/${id}/render_single_preview/`,
                    method: 'POST',
                    body: payload,
                };
            },
            transformResponse: (response, meta, arg) => response.data
        }),
        getRemainingCreds: builder.query({
            query: () => ({
                url: `/ai/video/remaining_credits`,
            }),
            transformResponse: (response, meta, arg) => response.data
        }), 
        createAvatar: builder.mutation({
            query: (formData) => {
                return {
                    url:  `/ai/video/personas_v2`,
                    method: 'POST',
                    body: formData,
                };
            },
            transformResponse: (response, meta, arg) => response.data
        }),
        getAvatars: builder.query({
            query: () => ({
                url: `/ai/video/personas_v2/`,
            }),
            transformResponse: (response, meta, arg) => response.data
        }), 
        getAvatarsCount: builder.query({
            query: () => ({
                url: `/ai/video/personas_v2/count`,
            }),
            transformResponse: (response, meta, arg) => response.data
        }), 
        removeAvatar: builder.mutation({
            query: AvatarId => ({
                url: `/ai/video/personas_v2/${AvatarId}`,
                method: 'DELETE',
            }),
        }),
        renderedVideos: builder.query({
            query: ({ currentPage }) => ({
                url: `/ai/video/link_to_videos/rendered_video?page=${currentPage}`,
            }),
            transformResponse: (response, meta, arg) => response.data
        }),
    })
})

export const { 
    useRemoveBackgroundMutation,
    useGenerateCreativeVariationsMutation,
    useGeneratePromptMutation,
    useGenerateBackgroundImageMutation,
    useGenerateBackgroundPromptMutation,
    useGetBackgroundImageStatusQuery,
    useUploadManualMutation,
    useGenerateVideoLinkMutation,
    useUpdateVideoLinkMutation,
    useGenerateVideoScriptMutation,
    useGetVideoAvatarsQuery,
    useGetVideoVoicesQuery,
    useGetVideoMusicsQuery,
    useGenerateVideoPreviewsMutation,
    useGetVideoStatusQuery,
    useRenderVideoMutation,
    useGetRemainingCredsQuery,
    useCreateAvatarMutation,
    useGetAvatarsQuery,
    useGetAvatarsCountQuery,
    useRemoveAvatarMutation,
    useRenderedVideosQuery
} = aiGenerationApi;